import { Component } from '@theme/component';

/**
 * StoreSearch web component.
 *
 * Renders a Google PlaceAutocompleteElement into a search container and optionally
 * a "Use my location" geolocation button. When a place is selected or geolocation
 * resolves, dispatches a `map:pan-to` CustomEvent that bubbles up to the parent
 * `<store-locator>` where `<store-map>` listens.
 *
 * Two possible `map:pan-to` detail shapes:
 *
 * 1. Viewport-based (city / region from PlaceAutocompleteElement):
 *    ```
 *    { viewport: google.maps.LatLngBounds }
 *    ```
 *    store-map.js calls `map.fitBounds(viewport)` to frame the area.
 *
 * 2. Coordinate-based (specific address or geolocation):
 *    ```
 *    { lat: number, lng: number, zoom: number }
 *    ```
 *    store-map.js calls `map.panTo({ lat, lng })` + `map.setZoom(zoom)`.
 *
 * @extends {Component}
 */
class StoreSearch extends Component {
  /**
   * Called when the element is connected to the document's DOM.
   *
   * Synchronous per the Web Components spec — async work is deferred to #init().
   */
  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  /**
   * Initialises the PlaceAutocompleteElement, appends it to the search container,
   * and wires up the geolocation button.
   *
   * @returns {Promise<void>}
   */
  async #init() {
    try {
      const { PlaceAutocompleteElement } = await google.maps.importLibrary('places');

      const autocomplete = new PlaceAutocompleteElement({});

      const searchContainer = this.querySelector('[ref="searchContainer"]');
      if (searchContainer) {
        searchContainer.appendChild(autocomplete);
      }

      autocomplete.addEventListener('gmp-select', (e) => this.#onPlaceSelect(e));

      // Attach geolocation button handler, or remove the button if unsupported
      if (!navigator.geolocation) {
        this.refs.geolocateBtn?.remove();
      } else {
        this.refs.geolocateBtn?.addEventListener('click', () => this.#handleGeolocate());
      }
    } catch (error) {
      // Non-critical — search is a progressive enhancement on top of the visible list + filter
      console.error('[Store Search]', error instanceof Error ? error.message : error);
    }
  }

  /**
   * Handles the `gmp-select` event from PlaceAutocompleteElement.
   *
   * Fetches place fields (location + viewport), then dispatches `map:pan-to`.
   * Prefers `viewport` for area-level places (cities, regions); falls back to
   * `location` + zoom 15 for specific addresses.
   *
   * @param {Event} event - The gmp-select event from PlaceAutocompleteElement.
   * @returns {Promise<void>}
   */
  async #onPlaceSelect(event) {
    try {
      const { placePrediction } = /** @type {any} */ (event);
      const place = placePrediction.toPlace();

      await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'viewport'] });

      if (place.viewport) {
        this.#dispatchPanTo({ viewport: place.viewport });
      } else {
        this.#dispatchPanTo({
          lat: place.location.lat(),
          lng: place.location.lng(),
          zoom: 15,
        });
      }
    } catch (error) {
      console.error('[Store Search] Place select error:', error instanceof Error ? error.message : error);
    }
  }

  /**
   * Triggers the browser geolocation prompt and dispatches `map:pan-to` on success.
   *
   * Silently degrades on error per SRCH-05 — no error UI is shown to the visitor.
   * Uses relaxed accuracy settings to improve speed and battery impact.
   */
  #handleGeolocate() {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.#dispatchPanTo({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          zoom: 12,
        });
      },
      (err) => {
        // SRCH-05: silent degradation — no error state shown to visitor
        console.warn('[Store Locator] Geolocation:', err.message);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  }

  /**
   * Dispatches a `map:pan-to` CustomEvent that bubbles up to `<store-locator>`.
   *
   * Supported payload shapes:
   * - `{ viewport: google.maps.LatLngBounds }` — city/region from autocomplete
   * - `{ lat: number, lng: number, zoom: number }` — address or geolocation point
   *
   * @param {{ viewport?: google.maps.LatLngBounds, lat?: number, lng?: number, zoom?: number }} payload
   */
  #dispatchPanTo(payload) {
    this.dispatchEvent(
      new CustomEvent('map:pan-to', {
        bubbles: true,
        detail: payload,
      })
    );
  }
}

if (!customElements.get('store-search')) {
  customElements.define('store-search', StoreSearch);
}
